/**
 * The standalone application. S8.8.
 *
 * The surface that makes D-01's whitelabel claim concrete for a customer with no CRM to embed into: a
 * representative signs in here directly and gets the same views the widget gets, over the same API, with a
 * provider bearer instead of a brokered token.
 *
 * ## Three states, and no router
 *
 * Signed out, the directory, and one exchange. Two locations, so `history.pushState` and a `popstate` listener
 * are the whole of the routing - the same decision the prospect surface made, and for the same reason: a
 * router would be a dependency earning its keep on two paths.
 *
 * The path is real rather than cosmetic. A representative can send somebody a link to an exchange, the back
 * button works, and a reload lands on the same place - which matters because a reload also signs in again.
 *
 * ## Signing in again after a reload is the design
 *
 * Nothing is persisted: the access token lives in memory and dies with the page. That costs a click and a
 * redirect the representative barely sees, because the identity provider's own session cookie is first-party
 * to it, and it buys the property that this origin's storage never holds anything granting access to a
 * document. `auth/session.ts` records the alternative that was rejected.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiClient, Button, Card, ExchangeView, FailureNotice, ThemeToggle } from '@iqz-ddds/components';
import { runtimeConfig, type UiConfig } from './config';
import { DirectoryScreen } from './screens/DirectoryScreen';
import {
  beginSignIn,
  completeSignIn,
  currentToken,
  refreshAccessToken,
  signedIn,
  signOut,
  type IdentityConfig,
} from './auth/session';

/** `/exchanges/{id}` or the directory. Two locations is what makes a router unnecessary. */
function exchangeIdFromPath(path: string): string | undefined {
  const match = /^\/exchanges\/([0-9a-fA-F-]{36})$/.exec(path);
  return match?.[1];
}

export function App(): React.JSX.Element {
  return <Application config={runtimeConfig} />;
}

type AuthState =
  | { readonly status: 'checking' }
  | { readonly status: 'signed_out'; readonly detail?: string }
  | { readonly status: 'signed_in' };

function Application({ config }: { readonly config: UiConfig }): React.JSX.Element {
  const identity: IdentityConfig = useMemo(
    () => ({ issuer: config.identityIssuer, clientId: config.identityClientId, scope: config.identityScope }),
    [config.identityIssuer, config.identityClientId, config.identityScope],
  );

  const [auth, setAuth] = useState<AuthState>({ status: 'checking' });
  const [path, setPath] = useState(globalThis.location.pathname);

  /**
   * Handle a callback if this load is one, on mount and only once.
   *
   * `completeSignIn` answers `not_a_callback` for an ordinary load, which is what makes calling it
   * unconditionally safe. On success the URL is replaced rather than navigated away from: an authorization code
   * left in the address bar is a spent credential in browser history, and a reload would try to exchange it a
   * second time.
   */
  useEffect(() => {
    void (async () => {
      try {
        const result = await completeSignIn(identity);
        if (result.kind === 'signed_in') {
          globalThis.history.replaceState(null, '', result.returnTo);
          setPath(result.returnTo);
          setAuth({ status: 'signed_in' });
          return;
        }
        if (result.kind === 'failed') {
          globalThis.history.replaceState(null, '', '/');
          setAuth({ status: 'signed_out', detail: result.detail });
          return;
        }
        setAuth(signedIn() ? { status: 'signed_in' } : { status: 'signed_out' });
      } catch (err) {
        // Discovery failed, which is the interesting case: a misspelled issuer, or no egress to the provider.
        // The message names it, because that is what somebody has to go and fix.
        setAuth({
          status: 'signed_out',
          detail: err instanceof Error ? err.message : 'The identity provider could not be reached.',
        });
      }
    })();
  }, [identity]);

  // The back button. Without this, navigating back leaves the address bar and the screen disagreeing.
  useEffect(() => {
    const onPop = (): void => setPath(globalThis.location.pathname);
    globalThis.addEventListener('popstate', onPop);
    return () => globalThis.removeEventListener('popstate', onPop);
  }, []);

  const go = useCallback((to: string) => {
    globalThis.history.pushState(null, '', to);
    setPath(to);
  }, []);

  const client = useMemo(
    () =>
      new ApiClient({
        baseUrl: config.apiBaseUrl,
        // Section 13's first credential: an identity provider access token, verified against the JWKS.
        scheme: 'Bearer',
        lapsedMessage: 'This session has ended. Sign in again.',
        token: () => currentToken(),
        refresh: async () => {
          const refreshed = await refreshAccessToken(identity);
          // A failed refresh means the session is over at the provider too, so the screen has to follow -
          // otherwise every later call fails in a way that looks like our bug rather than a lapsed sign-in.
          if (refreshed === undefined) setAuth({ status: 'signed_out' });
          return refreshed;
        },
      }),
    [config.apiBaseUrl, identity],
  );

  /** One place that turns a thrown error into something to render. Passed to the screens. */
  const describe = useCallback((err: unknown): { detail: string; reference?: string } => {
    const detail = err instanceof Error ? err.message : 'Something went wrong.';
    const reference = (err as { reference?: string }).reference;
    return { detail, ...(reference === undefined ? {} : { reference }) };
  }, []);

  if (auth.status === 'checking') {
    return (
      <main className="ddds-page ddds-page--wide">
        <p className="ddds-empty">Checking your sign-in.</p>
      </main>
    );
  }

  if (auth.status === 'signed_out') {
    return (
      <main className="ddds-page ddds-page--wide">
        <p className="ddds-eyebrow">Document exchange</p>
        <h1 className="ddds-title">Sign in</h1>
        <p className="ddds-lede">
          For representatives. Clients and prospects do not sign in here - they receive a link by email.
        </p>

        {auth.detail !== undefined && <FailureNotice detail={auth.detail} />}

        <Card>
          <p className="ddds-empty">
            You will be sent to your organisation&apos;s sign-in page and returned here. Nothing is kept on this
            device, so you will be asked again after a reload.
          </p>
          <div className="ddds-rep-add">
            <Button onClick={() => void beginSignIn(identity, path)}>Continue to sign in</Button>
          </div>
        </Card>
      </main>
    );
  }

  const exchangeId = exchangeIdFromPath(path);

  return (
    <div className="ddds-app">
      <Rail onHome={() => go('/')} onSignOut={() => void signOut(identity)} atHome={exchangeId === undefined} />

      <main className="ddds-main">
        {exchangeId === undefined ? (
          <>
            <h1 className="ddds-title">Your contacts</h1>
            <p className="ddds-lede">
              Everyone you exchange documents with. Opening a contact shows everything they have sent and received.
            </p>
            <DirectoryScreen client={client} onOpen={(id) => go(`/exchanges/${id}`)} onFailure={describe} />
          </>
        ) : (
          <>
            <p className="ddds-crumb">
              <a
                href="/"
                onClick={(event) => {
                  // A real href, so the link can be opened in a new tab and read by a screen reader as one,
                  // with the navigation intercepted for the ordinary click. `pushState` is the whole router.
                  event.preventDefault();
                  go('/');
                }}
              >
                Contacts
              </a>
              {' / '}
              <span className="ddds-crumb__here">This contact</span>
            </p>
            {/*
              An empty container class: the shell already supplies the geometry, so the view adds none.

              No `tabs` and no `view`: those exist for a host to state a preference through `init`, and this
              surface has no host, so a vertical strip or a non-default opening tab here would be a decision
              nobody made. The interval is different - it is a deployment's to set, not a host's.
            */}
            <ExchangeView
              client={client}
              exchangeId={exchangeId}
              className=""
              pollSeconds={config.pollSeconds}
              documentRequestMaxLength={config.documentRequestMaxLength}
            />
          </>
        )}
      </main>
    </div>
  );
}

/**
 * The rail, which is the whole of the application's chrome.
 *
 * One destination, and it is honest about that: DG.18's reference draws a dashboard, a requests queue, a
 * shares list and a cross-book file view, and not one of those projections exists - every read today is
 * scoped to one exchange, or to one contact since D-25. Rendering them greyed out would be four controls
 * that refuse to do anything, so they are simply absent until something answers them.
 *
 * Below 60rem this is a horizontal strip rather than a column; `surface.ts` owns that, so there is no
 * second component and no viewport check here.
 */
function Rail({
  onHome,
  onSignOut,
  atHome,
}: {
  readonly onHome: () => void;
  readonly onSignOut: () => void;
  readonly atHome: boolean;
}): React.JSX.Element {
  return (
    <nav className="ddds-rail" aria-label="Sections">
      <div className="ddds-mark">
        {/*
          The mark is text until DG.20 supplies an asset and somewhere to configure it. `branding.firmName`
          exists in the backend config and reaches a notification; no surface reads it yet, so hardcoding a
          customer name here would be the whitelabel mistake D-01 exists to prevent.
        */}
        <span className="ddds-mark__glyph" aria-hidden="true">
          D
        </span>
        <span className="ddds-mark__name">Documents</span>
      </div>

      <button className="ddds-rail__item" type="button" onClick={onHome} {...(atHome ? { 'aria-current': 'page' as const } : {})}>
        <span aria-hidden="true">&#9679;</span>
        Contacts
      </button>

      <span className="ddds-rail__spacer" />

      <div className="ddds-rail__foot">
        <ThemeToggle />
        <Button variant="quiet" size="small" onClick={onSignOut}>
          Sign out
        </Button>
      </div>
    </nav>
  );
}
